// Added by Jonghyung Lee on 2021-12-13
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, useState, useEffect } from 'react'

import { WPSMonitoringBox, WPSMonitoringDetail } from '../../WPSMonitoring'

// interface
import { WebComponentProps } from '@/helper/registerWebComponent'

import { reqGetWPSWorks } from '@/service/wps.service'

// Helper
import LegacyEvtHandler from '@/helper/LegacyEvtHandler'
import { EVT_UPDATE_CURRENT_DB, EVT_STUDYLIST_SELECTION } from '@/helper/define/legacyEventDefine'
import { WPSWorks } from '@/helper/define/wpsDefine'
import Logger from '@/helper/Logger'

// store
import useStore from '@/stores/global/hooks/useStore'

const WPSMonitoringBoxWrapper: FC<WebComponentProps> = ({
  customElementName,
  sendDataToCore,
  recvDataFromCore,
  classes,
  ...props
}) => {
  const {
    userAuth: { userName: username },
  } = useStore()

  const [curProxyToken, setCurProxyToken] = useState<string | null>(null)
  const [onGettingCount, setGettingCountStatus] = useState(false)

  const [waitingCount, setWaitingCount] = useState(0)
  const [waitingList, setWaitingList] = useState<WPSWorks[]>([])
  const [runningCount, setRunningCount] = useState(0)
  const [runningList, setRunningList] = useState<WPSWorks[]>([])
  const [isDetailDialogOpened, setDetailDialogOpened] = useState(false)

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const getWPS = async (force = false) => {
    if (!curProxyToken) return
    if (!force && onGettingCount) return

    try {
      setGettingCountStatus(true)

      const {
        data: { items },
      } = await reqGetWPSWorks({ username, data_token: curProxyToken, item_status: 'WAITING,RUNNING'  })

      if (!items) throw new Error('No items')
      const waiting = items.filter((item) => item.status === 'WAITING')
      const running = items.filter((item) => item.status === 'RUNNING')

      setWaitingList(waiting)
      setRunningList(running)
      setWaitingCount(waiting.length)
      setRunningCount(running.length)
    } catch (e) {
      Logger.errLog('WPS_MONITORING_BOX.getWPS : Failed')
      console.log(e)
    } finally {
      setGettingCountStatus(false)
    }
  }

  const updateCurrentDbEvtHandler = (event: any) => {
    try {
      const { detail: proxyToken } = event
      setCurProxyToken(proxyToken)
      getWPS(true)
    } catch (e) {
      Logger.errLog('WPS_MONITORING_BOX.handleBoxClick : Token is empty')
      return
    }
  }

  const studylistSelectEvtHandler = () => getWPS()

  const handleBoxClick = () => {
    if (runningCount === 0 && waitingCount === 0) return
    setDetailDialogOpened(true)
  }

  useEffect(() => {
    LegacyEvtHandler.addEvtListener(EVT_UPDATE_CURRENT_DB, updateCurrentDbEvtHandler)
    LegacyEvtHandler.addEvtListener(EVT_STUDYLIST_SELECTION, studylistSelectEvtHandler)

    let timeoutId: NodeJS.Timeout = setTimeout(async function reqWPS() {
      await getWPS()
      timeoutId = setTimeout(reqWPS, 2000)
    }, 2000)

    return () => {
      LegacyEvtHandler.removeEvtListener(EVT_UPDATE_CURRENT_DB, updateCurrentDbEvtHandler)
      LegacyEvtHandler.removeEvtListener(EVT_STUDYLIST_SELECTION, studylistSelectEvtHandler)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [curProxyToken])

  return (
    <>
      <div
        style={{
          boxSizing: 'border-box',
          backgroundColor: '#535353',
          overflowY: 'hidden',
          borderLeft: '2px solid #000',
          borderBottom: '1px solid #000',
          borderTop: '1px solid #000',
        }}
      >
        <WPSMonitoringBox
          classes={classes}
          waitingCount={waitingCount}
          runningCount={runningCount}
          onBoxClick={handleBoxClick}
        />
      </div>
      <WPSMonitoringDetail
        open={isDetailDialogOpened}
        waitingTasks={waitingList}
        runningTasks={runningList}
        proxyToken={curProxyToken}
        onCloseBtnClick={() => setDetailDialogOpened(false)}
      />
    </>
  )
}

export default WPSMonitoringBoxWrapper
